import React from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Send as SendIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  AttachFile as AttachFileIcon,
  Clear as ClearIcon,
  Stop as StopIcon, // Add missing import
} from '@mui/icons-material';

function ChatInputSection({
  question,
  setQuestion,
  onRequest,
  getAnswer,
  file,
  setFile,
  isListening,
  startListening,
  stopListening,
  handleStopTyping,
  darkMode,
  isMobile,
}) {
  let theme; let
    isMobileView;
  try {
    theme = useTheme();
    isMobileView = useMediaQuery ? useMediaQuery(theme.breakpoints.down('md')) : false;
  } catch (e) {
    theme = { breakpoints: { down: () => false } };
    isMobileView = false;
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      getAnswer();
    }
  };

  const handleRemoveFile = () => {
    // Create a synthetic event that clears the file
    setFile({ target: { files: [] } });
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        bgcolor: 'background.paper',
        borderTop: { xs: 1, sm: 0 },
        borderColor: 'divider',
        // Fix: Remove bottom padding that creates gap
        '@media (max-width: 768px)': {
          paddingBottom: '0px', // Remove padding
          // Only add safe area if needed
          marginBottom: 'env(safe-area-inset-bottom, 0px)',
        },
      }}
    >
      {/* File Upload Preview */}
      {file && (
        <Box sx={{
          mb: { xs: 1, sm: 2 },
          p: { xs: 1, sm: 2 },
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            📎
            {' '}
            {file.name}
          </Typography>
          <IconButton size="small" onClick={handleRemoveFile}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Input Row */}
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: { xs: 1, sm: 2 },
      }}
      >
        {/* File Upload Button - RESTORED to left side */}
        <input
          type="file"
          accept="image/*,.pdf"
          onChange={setFile}
          style={{ display: 'none' }}
          id="file-upload"
          data-testid="file-upload-input"
        />
        <label htmlFor="file-upload">
          <IconButton
            component="span"
            disabled={onRequest}
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' },
              '@media (max-width: 768px)': {
                width: 40,
                height: 40,
                padding: '8px',
              },
            }}
            data-testid="file-upload-btn"
          >
            <AttachFileIcon />
          </IconButton>
        </label>

        {/* Text Input */}
        <TextField
          multiline
          minRows={1}
          maxRows={4}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your question..."
          variant="outlined"
          disabled={onRequest}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: { xs: 3, sm: 4 },
              minHeight: { xs: 48, sm: 56 },
              // Fix: Proper mobile input styling
              '@media (max-width: 768px)': {
                fontSize: '16px', // Prevent zoom on iOS
              },
            },
            '& .MuiInputBase-input': {
              '@media (max-width: 768px)': {
                fontSize: '16px !important', // Force 16px to prevent zoom
                lineHeight: '1.5',
                padding: '12px 14px',
              },
            },
          }}
        />

        {/* Right Action Buttons */}
        <Box sx={{
          display: 'flex',
          gap: { xs: 0.5, sm: 1 },
          flexShrink: 0,
        }}
        >
          {/* Voice Button */}
          <IconButton
            onClick={isListening ? stopListening : startListening}
            disabled={onRequest}
            aria-label="Voice Input"
            sx={{
              color: isListening ? 'error.main' : 'text.secondary',
              bgcolor: isListening ? 'error.lighter' : 'transparent',
              '&:hover': {
                bgcolor: isListening ? 'error.light' : 'action.hover',
              },
              '@media (max-width: 768px)': {
                width: 40,
                height: 40,
              },
            }}
          >
            {isListening ? <MicOffIcon /> : <MicIcon />}
          </IconButton>

          {/* Send/Stop Button */}
          {onRequest ? (
            <IconButton
              onClick={handleStopTyping}
              aria-label="Stop Typing"
              sx={{
                bgcolor: 'error.main',
                color: 'error.contrastText',
                '&:hover': { bgcolor: 'error.dark' },
                '@media (max-width: 768px)': {
                  width: 40,
                  height: 40,
                },
              }}
            >
              <StopIcon />
            </IconButton>
          ) : (
            <IconButton
              onClick={getAnswer}
              disabled={!question.trim() || onRequest}
              aria-label="Send Message"
              sx={{
                bgcolor: question.trim() ? 'primary.main' : 'action.disabled',
                color: question.trim() ? 'primary.contrastText' : 'text.disabled',
                '&:hover': {
                  bgcolor: question.trim() ? 'primary.dark' : 'action.disabled',
                },
                '&:disabled': {
                  bgcolor: 'action.disabled',
                  color: 'text.disabled',
                },
                '@media (max-width: 768px)': {
                  width: 40,
                  height: 40,
                },
              }}
            >
              <SendIcon />
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default ChatInputSection;
